import React, { useEffect, useRef, useState } from 'react';
import { MusicTrack } from './MusicLibraryPicker';
import type { MusicSelection } from './MusicTrimModal';
import MusicTrimModal from './MusicTrimModal';
import { apiBaseUrl } from '../firebase';
import { buildMusicProxyUrl, fetchMusicProxyBlobUrl } from '../utils/vpsApi';

interface Props {
  open: boolean;
  videoFile: File;
  onClose: () => void;
  onSave: (blob: Blob) => void;
  maxDurationSeconds?: number;
  onBgMusicChange?: (track: MusicTrack | null) => void;
  initialCaption?: string;
  onCaptionChange?: (caption: string) => void;
}

const ReelEditorV2: React.FC<Props> = ({ open, videoFile, onClose, onSave, maxDurationSeconds = 120, onBgMusicChange, initialCaption = '', onCaptionChange }) => {
  const proxiedAudioURL = (url: string) => {
    return buildMusicProxyUrl(url);
  };
  const [videoUrl, setVideoUrl] = useState('');
  const [caption, setCaption] = useState(initialCaption);
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [muteOriginal, setMuteOriginal] = useState(false);
  const [origVol, setOrigVol] = useState(1);
  const [bgVol, setBgVol] = useState(1);
  // New Instagram-like music tool selection
  const [musicSel, setMusicSel] = useState<MusicSelection | null>(null);
  const [showMusicTool, setShowMusicTool] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bgDirectFallback, setBgDirectFallback] = useState(false); // when CORS blocks WebAudio
  const [showAudioHint, setShowAudioHint] = useState(false); // show hint when autoplay blocked
  const [isNarrow, setIsNarrow] = useState(false); // responsive layout toggle
  // Music trim UI state

  // Text overlay state
  const [text, setText] = useState('');
  const [textVisible, setTextVisible] = useState(false);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(28);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textBg, setTextBg] = useState(true);
  const [textBgColor, setTextBgColor] = useState('#000000');
  const [textBgAlpha, setTextBgAlpha] = useState(0.35);
  const [posXPct, setPosXPct] = useState(50);
  const [posYPct, setPosYPct] = useState(80);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const captionRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // Removed scrubbing state (unused) to silence ESLint warnings

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hiddenVideoRef = useRef<HTMLVideoElement | null>(null);
  const bgAudioRef = useRef<HTMLAudioElement | null>(null);
  const bgBlobUrlRef = useRef<string | null>(null);

  // Preview AudioContext for mixing in preview
  const acRef = useRef<AudioContext | null>(null);
  const vGainRef = useRef<GainNode | null>(null);
  const mGainRef = useRef<GainNode | null>(null);
  const vSrcRef = useRef<MediaElementAudioSourceNode | null>(null);
  const mSrcRef = useRef<MediaElementAudioSourceNode | null>(null);

  useEffect(() => {
    if (!open) return;
    const url = URL.createObjectURL(videoFile);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [open, videoFile]);

  useEffect(() => { if (open) setCaption(initialCaption || ''); }, [open, initialCaption]);

  // Setup preview audio routing once (route video via WebAudio; bg music uses direct playback for reliability)
  useEffect(() => {
    if (!open || !videoRef.current) return;
    const ac = new (window.AudioContext || (window as any).webkitAudioContext)();
    acRef.current = ac;
    try { videoRef.current.muted = true; } catch {}
    try { acRef.current?.resume?.(); } catch {}
    if (!vSrcRef.current) {
      const src = ac.createMediaElementSource(videoRef.current);
      const gain = ac.createGain();
      vSrcRef.current = src;
      vGainRef.current = gain;
      // Initialize to a neutral volume; a separate effect will apply the current settings
      gain.gain.value = 1;
      src.connect(gain).connect(ac.destination);
    }
    // Do not connect bg audio through WebAudio here; we will use direct element playback to avoid silent states
    return () => {
      try { vSrcRef.current?.disconnect(); } catch {}
      try { vGainRef.current?.disconnect(); } catch {}
      try { mSrcRef.current?.disconnect(); } catch {}
      try { mGainRef.current?.disconnect(); } catch {}
      vSrcRef.current = null; vGainRef.current = null; mSrcRef.current = null; mGainRef.current = null;
      try { ac.close(); } catch {}
      acRef.current = null;
    };
  }, [open, videoUrl]);

  // Track bg audio play/pause to drive indicator and persist audio-enabled
  // Track audio hint only; no chip indicator needed now
  useEffect(() => { if (!open) return; try { if (sessionStorage.getItem('audioEnabled')) setShowAudioHint(false); } catch {} }, [open]);

  // Simple responsive toggle for small screens
  useEffect(() => {
    const apply = () => setIsNarrow(window.innerWidth < 900);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  useEffect(() => { if (vGainRef.current) vGainRef.current.gain.value = muteOriginal ? 0 : origVol; }, [muteOriginal, origVol]);
  useEffect(() => { if (mGainRef.current) mGainRef.current.gain.value = bgVol; }, [bgVol]);
  // When using direct fallback, drive element volume
  useEffect(() => {
    if (bgDirectFallback && bgAudioRef.current) {
      try { bgAudioRef.current.volume = bgVol; } catch {}
    }
  }, [bgVol, bgDirectFallback]);

  const onLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const d = (e.currentTarget as HTMLVideoElement).duration || 0;
    setDuration(d);
    setStart(0);
    setEnd(Math.min(maxDurationSeconds, d));
  };

  // Apply a new music selection from the trim tool
  const applyMusicSelection = async (sel: MusicSelection) => {
    setMusicSel(sel);
    setShowMusicTool(false);
    onBgMusicChange?.(sel.track);
    const el = bgAudioRef.current; if (!el) return;

    if (!apiBaseUrl) {
      setError('Backend API not configured.');
      return;
    }

    const blobUrl = await fetchMusicProxyBlobUrl(sel.track.audioURL).catch(() => null);
    if (!blobUrl) {
      setError('Failed to load background music.');
      return;
    }

    if (bgBlobUrlRef.current) {
      try { URL.revokeObjectURL(bgBlobUrlRef.current); } catch {}
    }
    bgBlobUrlRef.current = blobUrl;

    el.src = blobUrl;
    el.preload = 'auto';
    (el as any).crossOrigin = 'anonymous';
    el.loop = false; // manual loop in the selected window
    setBgDirectFallback(true);
    try { el.volume = bgVol; } catch {}
    // Align to current video time within the clip window
    const v = videoRef.current;
    const rel = Math.max(0, (v?.currentTime || 0) - (start || 0));
    const offset = sel.clipSec > 0 ? (rel % sel.clipSec) : 0;
    const want = sel.startSec + offset;
    const seekAfterMeta = () => { try { el.currentTime = want; } catch {} };
    if (isFinite(el.duration) && el.duration > 0) { try { el.currentTime = want; } catch {} }
    else { try { el.addEventListener('loadedmetadata', seekAfterMeta, { once: true } as any); } catch { el.addEventListener('loadedmetadata', seekAfterMeta as any); } }
    // If video is already playing, start audio immediately to avoid delay
    if (v && !v.paused) {
      try { el.play(); } catch {}
    }
  };

  const clearBgTrack = () => {
    setMusicSel(null);
    setShowMusicTool(false);
    onBgMusicChange?.(null);
    const el = bgAudioRef.current;
    try { el?.pause(); } catch {}
    if (el) {
      try { el.removeAttribute('src'); el.load(); } catch {}
    }
    if (bgBlobUrlRef.current) {
      try { URL.revokeObjectURL(bgBlobUrlRef.current); } catch {}
      bgBlobUrlRef.current = null;
    }
    try { mSrcRef.current?.disconnect(); } catch {}
    try { mGainRef.current?.disconnect(); } catch {}
    mSrcRef.current = null; mGainRef.current = null;
    setBgDirectFallback(false);
  };

  // Unlock autoplay on first pointer interaction anywhere inside modal
  useEffect(() => {
    if (!open) return;
    const el = document;
    const onFirst = () => {
      try {
        acRef.current?.resume?.();
        const v = videoRef.current;
        const a = bgAudioRef.current;
        v?.play?.();
        if (a) {
          // briefly play muted to unlock, then pause to avoid audible blip
          const prevVol = a.volume;
          try { a.volume = 0; } catch {}
          a.play().then(() => { try { a.pause(); } catch {} }).catch(() => {});
          try { a.volume = prevVol; } catch {}
        }
      } catch {}
      try { sessionStorage.setItem('audioEnabled', '1'); } catch {}
      el.removeEventListener('pointerdown', onFirst, true);
    };
    el.addEventListener('pointerdown', onFirst, true);
    return () => { el.removeEventListener('pointerdown', onFirst, true); };
  }, [open]);

  // Keep music aligned and looped to the selected window during preview
  useEffect(() => {
    if (!open) return;
    const v = videoRef.current; const a = bgAudioRef.current; const sel = musicSel;
    if (!v || !a || !sel) return;
    const loopStart = () => sel.startSec;
    const loopEnd = () => sel.startSec + sel.clipSec;
    const sync = () => {
      const rel = Math.max(0, (v.currentTime || 0) - (start || 0));
      const offset = sel.clipSec > 0 ? (rel % sel.clipSec) : 0;
      const want = sel.startSec + offset;
      try { a.currentTime = want; } catch {}
    };
    // If the video is already playing when this effect attaches, start audio now
    if (!v.paused) {
      sync();
      try { a.play(); } catch {}
    }
    const onPlay = () => {
      sync();
      try {
        const p = a.play();
        if (p && typeof (p as any).then === 'function') { (p as Promise<void>).then(() => sync()).catch(() => {}); }
      } catch {}
    };
    const onPause = () => { try { a.pause(); } catch {} };
    const onSeeking = () => sync();
    const onSeeked = () => sync();
    const onTimeUpdate = () => {
      // manual loop inside window and guard drift
      try {
        if (a.currentTime < loopStart() || a.currentTime > loopEnd()) a.currentTime = loopStart();
        const rel = Math.max(0, (v.currentTime || 0) - (start || 0));
        const want = sel.startSec + (sel.clipSec > 0 ? (rel % sel.clipSec) : 0);
        if (Math.abs(a.currentTime - want) > 0.25) a.currentTime = want;
      } catch {}
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('seeking', onSeeking);
    v.addEventListener('seeked', onSeeked);
    v.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('seeking', onSeeking);
      v.removeEventListener('seeked', onSeeked);
      v.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [open, musicSel, start, videoUrl]);

  // (Old music offset sync removed; new logic above handles selection window looping)

  // Ensure after metadata loads we honor current selection
  useEffect(() => {
    if (!open) return;
    const el = bgAudioRef.current; const v = videoRef.current; const sel = musicSel;
    if (!el || !v || !sel) return;
    const onLoaded = () => {
      const rel = Math.max(0, (v.currentTime || 0) - (start || 0));
      const want = sel.startSec + (sel.clipSec > 0 ? (rel % sel.clipSec) : 0);
      try { el.currentTime = want; } catch {}
    };
    el.addEventListener('loadedmetadata', onLoaded);
    return () => { el.removeEventListener('loadedmetadata', onLoaded); };
  }, [open, musicSel, start]);

  // Re-encoding is slow and lossy, so skip it when nothing visual/audio changed.
  const hasEdits =
    start > 0.05 ||
    (duration > 0 && end < duration - 0.05) ||
    !!musicSel ||
    (textVisible && !!text.trim()) ||
    muteOriginal ||
    Math.abs(origVol - 1) > 0.01;

  const handleExport = async () => {
    if (!hasEdits) { onClose(); return; }
    if (!videoUrl) { setError('Video not loaded'); return; }
    setError(null); setExporting(true);
    try {
      const srcVideo = hiddenVideoRef.current; if (!srcVideo) throw new Error('Video element not ready');
      srcVideo.currentTime = start;
      await new Promise<void>((resolve) => { const onSeeked = () => { srcVideo.removeEventListener('seeked', onSeeked); resolve(); }; srcVideo.addEventListener('seeked', onSeeked); });

      // Prepare canvas composition so text overlay is burned into video
      const vw = srcVideo.videoWidth || 720; const vh = srcVideo.videoHeight || 1280;
      const canvas = document.createElement('canvas');
      canvas.width = vw; canvas.height = vh;
      const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Canvas 2D not available');

      // Setup audio mixing
      const ac = new (window.AudioContext || (window as any).webkitAudioContext)();
      const dest = ac.createMediaStreamDestination();
      if (!muteOriginal) {
        const vSrc = ac.createMediaElementSource(srcVideo);
        const vGain = ac.createGain(); vGain.gain.value = origVol; vSrc.connect(vGain).connect(dest);
      }
      // Add background music if selected, loop a trimmed window using BufferSource for precise looping
      if (musicSel?.track?.audioURL) {
        const resp = await fetch(proxiedAudioURL(musicSel.track.audioURL), { mode: 'cors' }).catch(() => null);
        if (resp && resp.ok) {
          const arr = await resp.arrayBuffer();
          const buf = await ac.decodeAudioData(arr.slice(0));
          const src = ac.createBufferSource();
          src.buffer = buf;
          src.loop = true;
          src.loopStart = Math.max(0, Math.min(buf.duration - 0.1, musicSel.startSec));
          src.loopEnd = Math.max(src.loopStart + 0.1, Math.min(buf.duration, musicSel.startSec + musicSel.clipSec));
          const mGain = ac.createGain(); mGain.gain.value = bgVol;
          src.connect(mGain).connect(dest);
          src.start(0, musicSel.startSec);
        }
      }

      // Capture canvas stream at 30fps
      const vStream = (canvas as any).captureStream ? (canvas as any).captureStream(30) : (canvas as any).mozCaptureStream?.(30);
      if (!vStream) throw new Error('Canvas captureStream not supported');
      const outStream = new MediaStream();
      const vTrack = vStream.getVideoTracks?.()[0]; if (vTrack) outStream.addTrack(vTrack);
      const aTrack = dest.stream.getAudioTracks()[0]; if (aTrack) outStream.addTrack(aTrack);

      const chunks: Blob[] = [];
      const mr = new MediaRecorder(outStream, { mimeType: 'video/webm;codecs=vp9,opus' });
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
      const stopPromise = new Promise<void>((resolve) => { mr.onstop = () => resolve(); });

      await ac.resume();
  srcVideo.play();
      mr.start(250);

      const clipMs = Math.min(maxDurationSeconds, Math.max(0, end - start)) * 1000;
      const startTs = performance.now();
      let rafId = 0;
      const draw = () => {
        // Draw video frame
        ctx.drawImage(srcVideo, 0, 0, vw, vh);
        // Draw text overlay if present
        if (textVisible && text.trim()) {
          const lines = text.split('\n');
          const style = `${textItalic ? 'italic ' : ''}${textBold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
          ctx.font = style; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          const x = (posXPct / 100) * vw; const y = (posYPct / 100) * vh;
          const lineHeight = fontSize * 1.25;
          const totalHeight = lineHeight * lines.length;
          if (textBg) {
            // measure widest line
            let maxW = 0; for (const L of lines) { const m = ctx.measureText(L); maxW = Math.max(maxW, m.width); }
            const padX = 16; const padY = 10;
            const bgW = maxW + padX * 2; const bgH = totalHeight + padY * 2;
            const rx = x - bgW / 2; const ry = y - bgH / 2;
            ctx.fillStyle = hexWithAlpha(textBgColor, textBgAlpha);
            ctx.fillRect(rx, ry, bgW, bgH);
          }
          ctx.fillStyle = textColor;
          lines.forEach((L, i) => {
            ctx.fillText(L, x, y - totalHeight / 2 + lineHeight * (i + 0.5));
          });
        }
        if (performance.now() - startTs < clipMs) { rafId = requestAnimationFrame(draw); }
      };
      draw();

      await new Promise((res) => setTimeout(res, clipMs));
      mr.stop();
      cancelAnimationFrame(rafId);
  srcVideo.pause();
      await stopPromise;

      const blob = new Blob(chunks, { type: 'video/webm' });
      onSave(blob); onClose();
    } catch (e: any) {
      console.error('Export failed', e);
      setError(e?.message || 'Failed to export');
    } finally { setExporting(false); }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
  <div style={{ ...styles.modal, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={{ margin: 0 }}>Create video</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
  <div style={{ ...styles.content, gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', minHeight: isNarrow ? 0 : styles.content.minHeight }}>
          <div style={styles.previewCol}>
            {videoUrl ? (
              <div style={{ position: 'relative', display: 'block' }}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  style={{ width: '100%', maxHeight: 420, borderRadius: 8, background: '#000', display: 'block' }}
                  onLoadedMetadata={onLoadedMetadata}
                  playsInline
                  controls
                />
                {/* Text overlay in preview */}
                {textVisible && (
                  <div
                    ref={overlayRef}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}
                  >
                    <div
                      ref={captionRef}
                      onPointerDown={(e) => {
                        setDragging(true);
                        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                        e.preventDefault();
                      }}
                      onPointerUp={(e) => {
                        setDragging(false);
                        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
                      }}
                      onPointerMove={(e) => {
                        if (!dragging) return;
                        const parent = (overlayRef.current?.parentElement as HTMLElement);
                        const rect = parent.getBoundingClientRect();
                        const node = captionRef.current as HTMLElement | null;
                        const elW = node?.offsetWidth || 0;
                        const elH = node?.offsetHeight || 0;
                        const halfW = elW / 2;
                        const halfH = elH / 2;
                        const px = Math.max(halfW, Math.min(rect.width - halfW, e.clientX - rect.left));
                        const py = Math.max(halfH, Math.min(rect.height - halfH, e.clientY - rect.top));
                        const x = (px / rect.width) * 100;
                        const y = (py / rect.height) * 100;
                        setPosXPct(Math.min(100, Math.max(0, x)));
                        setPosYPct(Math.min(100, Math.max(0, y)));
                      }}
                      style={{
                        position: 'absolute',
                        left: `${posXPct}%`,
                        top: `${posYPct}%`,
                        transform: `translate(-50%, -50%)`,
                        color: textColor,
                        fontFamily,
                        fontSize,
                        fontWeight: textBold ? 'bold' : 'normal',
                        fontStyle: textItalic ? 'italic' : 'normal',
                        padding: textBg ? '6px 10px' : 0,
                        background: textBg ? hexWithAlpha(textBgColor, textBgAlpha) : 'transparent',
                        borderRadius: 8,
                        userSelect: 'none',
                        cursor: 'move',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        textAlign: 'center',
                        maxWidth: '90%',
                        pointerEvents: 'auto'
                      }}
                    >
                      {text || 'Tap to add text'}
                    </div>
                  </div>
                )}
                <video ref={hiddenVideoRef} src={videoUrl} style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }} />
                <audio ref={bgAudioRef} style={{ display: 'none' }} />
              </div>
            ) : (
              <div style={{ width: '100%', maxHeight: 420, borderRadius: 8, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>Loading…</div>
            )}
            {!musicSel && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setShowMusicTool(true)} style={styles.secondaryBtn}>Add Music</button>
              </div>
            )}
            {musicSel && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 8, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden>♪</span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <strong style={{ fontSize: 13 }}>{musicSel.track.title}</strong>
                    <span style={{ fontSize: 12, color: '#5B6E6E' }}>{musicSel.track.artistName} • {Math.round(musicSel.clipSec)}s</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowMusicTool(true)} style={styles.ghostBtn}>Edit</button>
                  <button onClick={clearBgTrack} style={styles.ghostBtn}>Remove</button>
                </div>
              </div>
            )}
          </div>
          <div style={styles.controlsCol}>
            <div style={styles.row}>
              <label style={styles.label}>Description</label>
              <textarea value={caption} placeholder="Add description..." onChange={(e) => { setCaption(e.target.value); onCaptionChange?.(e.target.value); }} rows={2} style={styles.textarea} />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Trim start: {start.toFixed(1)}s</label>
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.05}
                value={start}
                // Removed unused scrubbing handlers
                onChange={(e) => {
                  const s = Number(e.target.value);
                  const newStart = Math.min(s, end);
                  setStart(newStart);
                  if (end - newStart > maxDurationSeconds) setEnd(newStart + maxDurationSeconds);
                }}
                onInput={(e: any) => {
                  const s = Number(e.currentTarget.value);
                  const newStart = Math.min(s, end);
                  setStart(newStart);
                  if (videoRef.current) { try { videoRef.current.currentTime = newStart; } catch {} }
                }}
                style={{ touchAction: 'none' }}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Trim end: {end.toFixed(1)}s</label>
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.05}
                value={end}
                // Removed unused scrubbing handlers
                onChange={(e) => {
                  const ne = Number(e.target.value);
                  const clamped = Math.max(ne, start);
                  setEnd(Math.min(clamped, start + maxDurationSeconds, duration));
                }}
                onInput={(e: any) => {
                  const ne = Number(e.currentTarget.value);
                  const clamped = Math.max(ne, start);
                  const v = Math.min(clamped, duration);
                  setEnd(Math.min(v, start + maxDurationSeconds));
                  if (videoRef.current) { try { videoRef.current.currentTime = Math.min(v, duration); } catch {} }
                }}
                style={{ touchAction: 'none' }}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}><input type="checkbox" checked={muteOriginal} onChange={(e) => setMuteOriginal(e.target.checked)} /> Mute original</label>
            </div>
            {!muteOriginal && (
              <div style={styles.row}>
                <label style={styles.label}>Original volume: {Math.round(origVol * 100)}%</label>
                <input type="range" min={0} max={1} step={0.01} value={origVol} onChange={(e) => setOrigVol(Number(e.target.value))} />
              </div>
            )}
            <div style={styles.row}>
              <label style={styles.label}>Background volume: {Math.round(bgVol * 100)}%</label>
              <input type="range" min={0} max={1} step={0.01} value={bgVol} onChange={(e) => setBgVol(Number(e.target.value))} />
            </div>
            {/* Removed separate music start slider; adjust by dragging the music chip */}
            {/* Removed duplicate offset control in the right column */}
            {/* Text overlay controls */}
            <div style={styles.row}>
              <label style={styles.label}>Caption text</label>
              <textarea
                value={text}
                placeholder="Add caption..."
                onChange={(e) => { setText(e.target.value); setTextVisible(!!e.target.value.trim()); }}
                onKeyDown={(e) => { e.stopPropagation(); }}
                onKeyUp={(e) => { e.stopPropagation(); }}
                rows={2}
                style={styles.textarea}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} style={styles.select}>
                  <option>Arial</option>
                  <option>Roboto</option>
                  <option>Georgia</option>
                  <option>Courier New</option>
                  <option>Impact</option>
                  <option>Poppins</option>
                  <option>Montserrat</option>
                  <option>Oswald</option>
                  <option>Lobster</option>
                </select>
                <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Size</label>
                <input type="range" min={12} max={96} step={1} value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} />
                <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Color</label>
                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
                <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}><input type="checkbox" checked={textBold} onChange={(e) => setTextBold(e.target.checked)} /> Bold</label>
                <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}><input type="checkbox" checked={textItalic} onChange={(e) => setTextItalic(e.target.checked)} /> Italic</label>
                <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}><input type="checkbox" checked={textBg} onChange={(e) => setTextBg(e.target.checked)} /> Background</label>
                {textBg && (<>
                  <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Bg</label>
                  <input type="color" value={textBgColor} onChange={(e) => setTextBgColor(e.target.value)} />
                  <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Opacity</label>
                  <input type="range" min={0} max={1} step={0.01} value={textBgAlpha} onChange={(e) => setTextBgAlpha(Number(e.target.value))} />
                </>)}
              </div>
            </div>
            {/* Music start offset control relocated to preview column below the selected music chip */}

            {error && <div style={{ color: '#b71c1c' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleExport} disabled={exporting} style={styles.primaryBtn}>{exporting ? 'Saving…' : hasEdits ? 'Save video' : 'Done'}</button>
              <button onClick={onClose} style={styles.ghostBtn}>Cancel</button>
            </div>
          </div>
        </div>
        {showAudioHint && (
          <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: 'rgba(7,43,44,0.9)', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
            Tap to enable sound
          </div>
        )}
      </div>
      {showMusicTool && (
        <MusicTrimModal
          open={showMusicTool}
          onClose={() => setShowMusicTool(false)}
          onConfirm={applyMusicSelection}
          initial={musicSel || undefined}
        />
      )}
    </div>
  );
};

export default ReelEditorV2;

// formatTime removed (unused)

// Helper to apply alpha on hex color like #RRGGBB
function hexWithAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  const clean = hex.replace('#', '');
  if (clean.length === 6) return `#${clean}${a}`;
  if (clean.length === 8) return `#${clean.substring(0, 6)}${a}`;
  return hex;
}

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal: { background: '#fff', borderRadius: 12, width: '95%', maxWidth: 1000, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 30px rgba(0,0,0,0.35)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #eee' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' },
  content: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: 16, minHeight: 540 },
  previewCol: { position: 'relative', display: 'flex', flexDirection: 'column' },
  controlsCol: { display: 'flex', flexDirection: 'column', gap: 12 },
  row: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: '#333' },
  textarea: { width: '100%', border: '1px solid #ddd', borderRadius: 8, padding: 10, resize: 'vertical', fontFamily: 'inherit' },
  select: { border: '1px solid #ddd', borderRadius: 8, padding: '8px 10px', background: '#fff' },
  primaryBtn: { padding: '10px 16px', background: '#0B6B6F', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
  secondaryBtn: { padding: '10px 16px', background: '#17A2A9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
  ghostBtn: { padding: '10px 16px', background: 'transparent', color: '#0B6B6F', border: '1px solid #0B6B6F', borderRadius: 8, cursor: 'pointer' },
};
